import { isBuiltInPath } from './routes';

/**
 * Link targets offered by the builder's page-link picker. The storefront
 * entries mirror `pathForView` in ./routes.ts so a picker choice always points
 * at a page the published site actually renders.
 */
export interface SiteLinkOption {
  label: string;
  href: string;
  group: string;
}

export const STOREFRONT_LINKS: SiteLinkOption[] = [
  { label: 'Home', href: '/', group: 'Storefront' },
  { label: 'Vehicles', href: '/vehicles', group: 'Storefront' },
  { label: 'Locations', href: '/locations', group: 'Storefront' },
  { label: 'Deals', href: '/deals', group: 'Storefront' },
  { label: 'Services', href: '/services', group: 'Storefront' },
  { label: 'About', href: '/about', group: 'Storefront' },
  { label: 'Contact', href: '/contact', group: 'Storefront' },
  { label: 'FAQ', href: '/faq', group: 'Storefront' },
  { label: 'Privacy policy', href: '/legal/privacy', group: 'Storefront' },
  { label: 'Terms', href: '/legal/terms', group: 'Storefront' },
  { label: 'My account', href: '/account', group: 'Storefront' },
];

export interface SitePageLink {
  slug: string;
  name: string;
  isPublished?: boolean;
}

/** Custom pages first, then the built-in storefront routes. */
export function siteLinkOptions(pages: SitePageLink[]): SiteLinkOption[] {
  const custom = pages
    .filter((page) => page.slug && page.slug !== 'home')
    .map((page) => ({
      label: `${page.name || page.slug}${page.isPublished === false ? ' (draft)' : ''}`,
      href: `/${page.slug}`,
      group: 'Your pages',
    }));
  return [...custom, ...STOREFRONT_LINKS];
}

export type LinkIssueKind = 'draft' | 'missing';

export interface LinkIssue {
  href: string;
  kind: LinkIssueKind;
  slug: string;
  label: string;
}

export interface LinkStatus {
  kind: 'ok' | 'external' | LinkIssueKind;
  href: string;
  slug?: string;
  label?: string;
}

function internalSlug(href: string): string | null {
  const path = href.split('#')[0].split('?')[0] || '/';
  const match = /^\/([a-z0-9]+(?:-[a-z0-9]+)*)\/?$/.exec(path);
  return match ? match[1] : null;
}

/**
 * Classifies an internal link against the agency's page list. Built-in
 * storefront routes always resolve; custom pages must exist and be published.
 */
export function linkStatus(href: string, pages: SitePageLink[]): LinkStatus {
  const value = href.trim();
  if (!value || value === '#') return { kind: 'ok', href: value };
  if (!value.startsWith('/') || value.startsWith('//')) {
    return { kind: 'external', href: value };
  }

  const path = value.split('#')[0].split('?')[0] || '/';
  if (isBuiltInPath(path)) return { kind: 'ok', href: value };

  const slug = internalSlug(value);
  if (!slug) return { kind: 'ok', href: value };
  if (slug === 'home') {
    return { kind: 'missing', href: value, slug, label: 'the home page is at /' };
  }

  const page = pages.find((candidate) => candidate.slug === slug);
  if (!page) {
    return { kind: 'missing', href: value, slug, label: slug };
  }
  if (page.isPublished === false) {
    return { kind: 'draft', href: value, slug, label: page.name || slug };
  }

  return { kind: 'ok', href: value, slug, label: page.name || slug };
}

/**
 * Walks a page section tree and returns every internal link that would 404
 * because its target page is missing or still a draft.
 */
export function collectLinkIssues(value: unknown, pages: SitePageLink[]): LinkIssue[] {
  const issues = new Map<string, LinkIssue>();

  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== 'object') return;

    for (const [key, child] of Object.entries(node)) {
      if ((key === 'href' || /Href$/.test(key)) && typeof child === 'string') {
        const status = linkStatus(child, pages);
        if (status.kind === 'draft' || status.kind === 'missing') {
          const href = child.trim();
          if (!issues.has(href)) {
            issues.set(href, {
              href,
              kind: status.kind,
              slug: status.slug ?? '',
              label: status.label ?? href,
            });
          }
        }
      } else {
        walk(child);
      }
    }
  };

  walk(value);
  return [...issues.values()];
}
